import gsap from 'gsap';
import imagesLoaded from 'imagesloaded';

// ─────────────────────────────────────────────
// Transition de page standard — l'ouverture par le logo
//
//  1. Fermeture : un calque sombre couvre l'écran, percé d'un « trou » qui a la forme
//     du contour du logo. Le trou part du plein écran et se resserre sur le centre :
//     l'ancienne page n'est plus visible qu'à l'intérieur du logo.
//  2. Relais : quand le trou atteint la taille du logo, le logo tracé apparaît pile
//     sur son bord, puis le trou se referme ; seul le contour reste.
//     Le nom de la page de destination arrive dessous en scramble, un trait fin
//     suit le chargement réel des images.
//  3. Ouverture : la nouvelle page apparaît dans le logo, qui s'agrandit jusqu'au
//     plein écran — le même geste que l'intro de la home.
//
// Aucun transform sur les conteneurs de page : les scènes épinglées (ScrollTrigger)
// ne sont jamais perturbées. Le trou est un chemin SVG en evenodd, son bord est tracé en trait fin.
// Les autres transitions (article, Urban Yellow, explosion) ne passent pas ici.
// ─────────────────────────────────────────────

const CLOSE_DUR = 0.85;   // plein écran → taille du logo
const SEAL_DUR  = 0.35;   // taille du logo → fermé
const OPEN_DUR  = 0.35;   // fermé → taille du logo
const GROW_DUR  = 0.95;   // taille du logo → plein écran
const MIN_LOAD  = 0.3;    // durée minimale de la phase de chargement (s)
const SCRAMBLE  = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Contour extérieur du logo (rect + polygone), coordonnées du viewBox 22.7353 × 23.5258
const LOGO_VB  = { w: 22.7353, h: 23.5258 };
const LOGO_PTS = [[0.5, 8.7003], [8.5077, 8.7003], [8.5077, 0.6925], [22.2353, 5.2423], [22.2353, 23.0258], [0.5, 23.0258]];

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let coverPromise = null;

const getEls = () => {
    const root = document.getElementById('page-curtain');
    if (!root) return null;
    return {
        root,
        svg:     root.querySelector('.pc-svg'),
        shape:   root.querySelector('.pc-shape'),
        edge:    root.querySelector('.pc-edge'),
        inner:   root.querySelector('.pc-inner'),
        logo:    root.querySelector('.pc-logo'),
        strokes: [...root.querySelectorAll('[data-pc-stroke]')],
        label:   root.querySelector('.pc-label > span'),
        bar:     root.querySelector('.pc-bar > i'),
    };
};

// ─────────────────────────────────────────────
// Géométrie du trou
// Le calque est un SVG plein écran : un chemin sombre (cadre de l'écran + anneau de
// 6 points en evenodd → l'anneau est un trou) et un polygone qui dessine le bord du trou.
// L'anneau est interpolé point par point : même nombre de points à chaque étape.
// ─────────────────────────────────────────────

const fmt = (n) => n.toFixed(1);

// Repère du calque : sa boîte réelle (et non window.innerWidth, qui inclut la barre de défilement :
// avec scrollbar-gutter: stable, le calque est plus étroit et la forme serait comprimée → décalage avec le logo)
const frame = () => {
    const svg = document.querySelector('#page-curtain .pc-svg');
    const r = svg ? svg.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    return { x: r.left, y: r.top, W: r.width, H: r.height };
};

const applyRing = (els, ring) => {
    const { W, H } = frame();
    els.svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    const pts = ring.map(([x, y]) => `${fmt(x)} ${fmt(y)}`);
    els.shape.setAttribute('d', `M0 0H${W}V${H}H0Z M${pts.join(' L')}Z`);
    els.edge.setAttribute('points', pts.join(' '));
};

// Anime l'anneau d'une forme à une autre (fonctions évaluées au départ du tween)
const ringTween = (els, fromFn, toFn, vars) => {
    const proxy = { t: 0 };
    let from, to;
    return gsap.to(proxy, {
        t: 1,
        ...vars,
        onStart: () => { from = fromFn(); to = toFn(); },
        onUpdate: () => {
            if (!from) { from = fromFn(); to = toFn(); }
            applyRing(els, from.map(([x, y], i) => [x + (to[i][0] - x) * proxy.t, y + (to[i][1] - y) * proxy.t]));
        },
    });
};

// Trou = plein écran (même répartition des points que l'intro de la home)
const ringFull = () => {
    const { W, H } = frame();
    const x1 = W * 0.37, m = 2;
    return [[-m, -m], [x1, -m], [x1, -m], [W + m, -m], [W + m, H + m], [-m, H + m]];
};

// Trou = contour du logo, calé sur le logo SVG du rideau
const ringLogo = (logo) => {
    const f  = frame();
    const b  = logo.getBoundingClientRect();
    const k  = Math.min(b.width / LOGO_VB.w, b.height / LOGO_VB.h);
    const ox = b.left - f.x + (b.width - LOGO_VB.w * k) / 2;
    const oy = b.top - f.y + (b.height - LOGO_VB.h * k) / 2;
    return LOGO_PTS.map(([x, y]) => [ox + x * k, oy + y * k]);
};

// Trou = fermé, ramené au centre du logo
const ringClosed = (logo) => {
    const f = frame();
    const b = logo.getBoundingClientRect();
    const c = [b.left - f.x + b.width / 2, b.top - f.y + b.height * 0.62];
    return LOGO_PTS.map(() => [...c]);
};

// ── Nom de la page de destination ──
const decode = (str) => {
    const t = document.createElement('textarea');
    t.innerHTML = str;
    return t.value;
};

export const getPageLabel = (data) => {
    const path = (data?.next?.url?.path || '').replace(/\/+$/, '');
    if (path === '') return 'Accueil';

    // Lien du menu : texte d'origine (aria-label est posé pendant le scramble au survol)
    const trigger = data?.trigger;
    if (trigger && trigger.nodeType === 1) {
        const txt = (trigger.getAttribute('aria-label') || trigger.textContent || '').trim();
        if (txt && txt.length <= 28) return txt;
    }

    // Sinon : <title> de la page suivante, première partie (« Info – Cédric Stoecklin » → « Info »)
    const m = /<title[^>]*>([^<]*)<\/title>/i.exec(data?.next?.html || '');
    if (m) {
        const title = decode(m[1]).split(/\s[–—|-]\s/)[0].trim();
        if (title) return title;
    }
    return '';
};

// ── Scramble du nom (même esprit que le loader et le menu) ──
const scrambleTo = (el, text, duration = 0.6) => {
    const target = text.toUpperCase();
    const proxy  = { p: 0 };
    return gsap.to(proxy, {
        p: 1,
        duration,
        ease: 'power2.out',
        onUpdate: () => {
            const reveal = Math.floor(target.length * proxy.p);
            let out = '';
            for (let i = 0; i < target.length; i++) {
                const c = target[i];
                out += (c === ' ' || i < reveal) ? c : SCRAMBLE[Math.floor(Math.random() * SCRAMBLE.length)];
            }
            el.textContent = out;
        },
        onComplete: () => { el.textContent = target; },
    });
};

// ─────────────────────────────────────────────
// 1. Fermeture — appelée par leave() ET enter() (mode sync de Barba) :
//    une seule animation, la même promesse pour les deux.
// ─────────────────────────────────────────────

export const curtainCover = (data) => {
    if (coverPromise) return coverPromise;

    const els = getEls();
    if (!els || !els.svg || !els.logo) return (coverPromise = Promise.resolve());

    const label = getPageLabel(data);

    coverPromise = new Promise((resolve) => {
        gsap.killTweensOf([els.root, els.svg, els.edge, els.inner, els.logo, els.label, els.bar, ...els.strokes].filter(Boolean));
        gsap.set(els.root, { autoAlpha: 1 });
        gsap.set(els.inner, { autoAlpha: 1 });
        gsap.set(els.logo, { autoAlpha: 1 });
        gsap.set(els.strokes, { strokeDashoffset: 1 });
        gsap.set(els.edge, { autoAlpha: 1 });
        if (els.bar) gsap.set(els.bar, { scaleX: 0, autoAlpha: 1 });
        if (els.label) {
            els.label.textContent = label.toUpperCase();
            gsap.set(els.label, { yPercent: 110, autoAlpha: 1 });
        }

        if (reducedMotion()) {
            applyRing(els, ringClosed(els.logo));
            gsap.set(els.edge, { autoAlpha: 0 });
            gsap.set(els.strokes, { strokeDashoffset: 0 });
            if (els.label) gsap.set(els.label, { yPercent: 0 });
            gsap.fromTo(els.svg, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, onComplete: resolve });
            return;
        }

        gsap.set(els.svg, { autoAlpha: 1 });
        applyRing(els, ringFull());

        const tl = gsap.timeline({ onComplete: resolve });

        // Le trou se resserre sur le logo : l'ancienne page n'est plus visible qu'à l'intérieur,
        // le bord du trou est dessiné par un trait fin
        tl.add(ringTween(els, ringFull, () => ringLogo(els.logo), { duration: CLOSE_DUR, ease: 'expo.inOut' }), 0)
          // Le logo se trace sur le bord au moment où le trou arrive à taille
          .to(els.strokes, { strokeDashoffset: 0, duration: 0.5, ease: 'power2.inOut', stagger: 0.05 }, CLOSE_DUR - 0.3)
          // Puis le trou se referme : seul le logo reste
          .add(ringTween(els, () => ringLogo(els.logo), () => ringClosed(els.logo), { duration: SEAL_DUR, ease: 'power3.in' }), CLOSE_DUR + 0.05)
          .to(els.edge, { autoAlpha: 0, duration: 0.2 }, CLOSE_DUR + 0.05 + SEAL_DUR - 0.15);

        if (els.label) {
            tl.to(els.label, { yPercent: 0, duration: 0.7, ease: 'expo.out' }, CLOSE_DUR + 0.1)
              .add(scrambleTo(els.label, label, 0.6), CLOSE_DUR + 0.1);
        }
    });

    return coverPromise;
};

// ─────────────────────────────────────────────
// 2. Chargement — le trait sous le nom suit la progression réelle des images
// ─────────────────────────────────────────────

// N'attend que les images visibles à l'ouverture (premier écran) : les autres
// (carrousels hors champ, index, galeries plus bas) se chargent ensuite, sans bloquer le rideau.
const isAboveFold = (img) => {
    const r = img.getBoundingClientRect();
    return r.width > 0 && r.top < window.innerHeight && r.bottom > 0 && r.left < window.innerWidth && r.right > 0;
};

export const loadWithProgress = (container, timeout = 2500) => {
    const els   = getEls();
    const start = performance.now();
    const barTo = (p, duration) => { if (els?.bar) gsap.to(els.bar, { scaleX: p, duration, ease: 'power2.out', overwrite: true }); };

    return new Promise((resolve) => {
        const finish = () => {
            const elapsed = (performance.now() - start) / 1000;
            const rest    = Math.max(0.2, MIN_LOAD - elapsed);
            barTo(1, rest);
            gsap.delayedCall(rest, resolve);
        };

        const imgs = [...container.querySelectorAll('img')].filter((img) => img.loading !== 'lazy' && !img.complete && isAboveFold(img));
        if (!imgs.length) { finish(); return; }

        let done = false;
        const timer = setTimeout(() => { if (!done) { done = true; finish(); } }, timeout);

        imagesLoaded(imgs)
            .on('progress', (inst) => barTo(inst.progressedCount / imgs.length * 0.9, 0.4))
            .on('always', () => {
                if (done) return;
                done = true;
                clearTimeout(timer);
                finish();
            });
    });
};

// ─────────────────────────────────────────────
// 3. Ouverture — la nouvelle page apparaît dans le logo, qui s'agrandit au plein écran
// ─────────────────────────────────────────────

export const curtainReveal = (next) => {
    const els = getEls();
    coverPromise = null;
    if (next) gsap.set(next, { opacity: 1 });
    if (!els || !els.svg || !els.logo) return Promise.resolve();

    return new Promise((resolve) => {
        const done = () => {
            gsap.set(els.root, { autoAlpha: 0 });
            resolve();
        };

        if (reducedMotion()) {
            gsap.to(els.root, { autoAlpha: 0, duration: 0.3, onComplete: done });
            return;
        }

        const tl = gsap.timeline({ onComplete: done });

        // Le nom et le trait de chargement s'effacent
        if (els.label) tl.to(els.label, { yPercent: -110, duration: 0.4, ease: 'power2.in' }, 0);
        if (els.bar) tl.to(els.bar, { autoAlpha: 0, duration: 0.3 }, 0);

        // Le trou s'ouvre depuis le centre jusqu'au contour du logo…
        tl.set(els.edge, { autoAlpha: 1 }, 0.2)
          .add(ringTween(els, () => ringClosed(els.logo), () => ringLogo(els.logo), { duration: OPEN_DUR, ease: 'power2.out' }), 0.2)
          // …puis s'agrandit jusqu'au plein écran : le trait du bord file vers les bords de l'écran
          .add(ringTween(els, () => ringLogo(els.logo), ringFull, { duration: GROW_DUR, ease: 'expo.inOut' }), 0.2 + OPEN_DUR)
          .to(els.logo, { autoAlpha: 0, duration: 0.3, ease: 'power1.out' }, 0.2 + OPEN_DUR + 0.05)
          .to(els.edge, { autoAlpha: 0, duration: 0.35, ease: 'power1.in' }, 0.2 + OPEN_DUR + GROW_DUR - 0.35);
    });
};

export const resetCurtain = () => { coverPromise = null; };
